import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, CheckCircle, AlertTriangle, ShieldAlert, XCircle, RefreshCw } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useSimulationDetail } from '@/hooks/useSimulations'
import { evaluateDecisionSignal } from '@/lib/calculations/decisionRules'

const formatIDR = (amount: number) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
}

const renderSignalIcon = (signal: string) => {
  switch (signal) {
    case 'continue': return <CheckCircle className="text-emerald-500" size={32} />
    case 'improve': return <RefreshCw className="text-blue-500" size={32} />
    case 'restructure': return <AlertTriangle className="text-amber-500" size={32} />
    case 'pivot': return <AlertTriangle className="text-orange-500" size={32} />
    case 'close': return <XCircle className="text-rose-500" size={32} />
    default: return <ShieldAlert className="text-slate-500" size={32} />
  }
}

export function SimulationDetailPage() {
  const { businessId, simId } = useParams<{ businessId: string, simId: string }>()
  const { data, isLoading, error } = useSimulationDetail(businessId, simId)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Menghitung hasil simulasi...</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
        <p>Gagal memuat detail: {error}</p>
      </div>
    )
  }

  const { run, months } = data

  // Prepare chart data
  const chartData = months.map(m => ({
    name: `Bln ${m.monthNumber}`,
    'Cash (Kas)': m.endingCash,
    'Equity (Modal)': m.endingEquity,
    'Net Profit': m.netProfit
  }))

  const stopLossBreached = months.some(m => m.status === 'stop_loss' || m.status === 'insolvent')
  const finalMonth = months[months.length - 1]

  const positiveProfitMonths = months.filter(m => m.netProfit > 0).length
  const decisionSignal = evaluateDecisionSignal({
    totalMonths: run.durationMonths,
    positiveProfitMonths,
    finalEquity: finalMonth.endingEquity,
    stopLossEquity: run.stopLossEquity,
    isAboveBepFinalMonth: finalMonth.isAboveBep === 1
  })

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={`/businesses/${businessId}/simulations`} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-slate-100">{run.name}</h1>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider bg-slate-800 text-slate-300 border border-slate-700">
              {run.type}
            </span>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Simulasi {run.durationMonths} Bulan. {run.notes}
          </p>
        </div>
      </div>

      {stopLossBreached && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-lg flex items-start gap-3">
          <ShieldAlert className="text-rose-500 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-rose-400">Peringatan Stop-loss Terpicu!</h3>
            <p className="text-sm text-rose-300/80 mt-1">
              Dalam simulasi ini, batas kerugian yang dapat ditolerir (Affordable Loss) terlewati. 
              Posisi ekuitas jatuh di bawah batas aman ({formatIDR(run.stopLossEquity)}).
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400 mb-1">Keputusan Akhir (AI)</p>
            <p className="text-xl font-bold text-slate-100 capitalize">{decisionSignal}</p>
          </div>
          {renderSignalIcon(decisionSignal)}
        </div>

        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm">
          <p className="text-sm text-slate-400 mb-1">Akhir Kas (M${run.durationMonths})</p>
          <p className={`text-xl font-bold ${finalMonth.endingCash < 0 ? 'text-rose-500' : 'text-emerald-400'}`}>
            {formatIDR(finalMonth.endingCash)}
          </p>
          <p className="text-xs text-slate-500 mt-2">Awal: {formatIDR(run.initialCash)}</p>
        </div>

        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm">
          <p className="text-sm text-slate-400 mb-1">Akhir Ekuitas / Nilai Bisnis</p>
          <p className={`text-xl font-bold ${finalMonth.endingEquity <= run.stopLossEquity ? 'text-rose-500' : 'text-slate-100'}`}>
            {formatIDR(finalMonth.endingEquity)}
          </p>
          <p className="text-xs text-slate-500 mt-2">Awal: {formatIDR(run.initialEquity)}</p>
        </div>

        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm">
          <p className="text-sm text-slate-400 mb-1">Total Laba Bersih</p>
          <p className={`text-xl font-bold ${finalMonth.accumulatedProfit < 0 ? 'text-rose-500' : 'text-emerald-400'}`}>
            {formatIDR(finalMonth.accumulatedProfit)}
          </p>
          <p className="text-xs text-slate-500 mt-2">Akumulasi {run.durationMonths} bulan</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-200 mb-6">Tren Kas & Ekuitas</h2>
        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `Rp${(value/1000000).toFixed(0)}M`} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '8px' }}
                itemStyle={{ fontSize: '13px' }}
                formatter={(value: number) => formatIDR(value)}
              />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
              <Line type="monotone" dataKey="Cash (Kas)" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Equity (Modal)" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Net Profit" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detail Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-slate-200">Hasil Kalkulasi Engine (Read-only)</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-slate-800/50 text-slate-300 border-b border-slate-700">
              <tr>
                <th className="px-4 py-3 font-medium">Bulan</th>
                <th className="px-4 py-3 font-medium text-emerald-400">Revenue</th>
                <th className="px-4 py-3 font-medium">Gross Profit</th>
                <th className="px-4 py-3 font-medium">Net Profit</th>
                <th className="px-4 py-3 font-medium">Acc. Profit</th>
                <th className="px-4 py-3 font-medium text-blue-400">End Cash</th>
                <th className="px-4 py-3 font-medium text-purple-400">End Equity</th>
                <th className="px-4 py-3 font-medium">BEP Rev</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {months.map((m) => (
                <tr key={m.id} className="hover:bg-slate-800/30 transition-colors text-slate-300">
                  <td className="px-4 py-3 font-medium text-center">{m.monthNumber}</td>
                  <td className="px-4 py-3 text-emerald-400">{formatIDR(m.revenue)}</td>
                  <td className="px-4 py-3">{formatIDR(m.grossProfit)}</td>
                  <td className={`px-4 py-3 ${m.netProfit < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                    {formatIDR(m.netProfit)}
                  </td>
                  <td className={`px-4 py-3 ${m.accumulatedProfit < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                    {formatIDR(m.accumulatedProfit)}
                  </td>
                  <td className={`px-4 py-3 font-medium ${m.endingCash < 0 ? 'text-rose-400' : 'text-blue-400'}`}>
                    {formatIDR(m.endingCash)}
                  </td>
                  <td className={`px-4 py-3 font-medium ${m.endingEquity <= run.stopLossEquity ? 'text-rose-400' : 'text-purple-400'}`}>
                    {formatIDR(m.endingEquity)}
                  </td>
                  <td className="px-4 py-3">{formatIDR(m.bepRevenue)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider
                      ${m.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-400' :
                        m.status === 'warning' ? 'bg-amber-500/10 text-amber-400' :
                        m.status === 'critical' ? 'bg-orange-500/10 text-orange-400' :
                        'bg-rose-500/10 text-rose-400'}
                    `}>
                      {m.status.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
